import { create, type StoreApi } from 'zustand';
import { dexieStorage } from '../data/dexieStorage';
import { packProjectFile, unpackProjectFile } from '../data/projectFile';
import type { ProjectStorage } from '../data/storage';
import type {
  Class,
  ClassCaptureSettings,
  EvalSample,
  NewProjectInput,
  Project,
  Sample,
  TrainedModel,
} from '../data/types';
import type { BlockProgram } from '../behavior/types';
import { bucketAccuracy, bucketDuration, bucketLength, capture, shortHash } from '../lib/analytics';
import type { AudioTrainer } from '../ml/audioTrainer';
import type { ImageTrainer, TrainHyperparams, TrainProgress } from '../ml/trainer';

type Timeout = ReturnType<typeof setTimeout>;

/**
 * Discriminated union of all soft-deletes the studio supports.
 *
 * One pending delete at a time; scheduling a new one commits the previous
 * immediately. The UI reads `pendingDelete.kind` to decide which Undo toast
 * to render and where (Home vs. ProjectView).
 */
export type PendingDelete =
  | {
      kind: 'project';
      project: Project;
      expiresAt: number;
      timeoutHandle: Timeout;
    }
  | {
      kind: 'class';
      projectId: string;
      class: Class;
      classIndex: number;
      expiresAt: number;
      timeoutHandle: Timeout;
    }
  | {
      kind: 'samples-batch';
      classId: string;
      samples: Sample[];
      expiresAt: number;
      timeoutHandle: Timeout;
    };

export interface ClassesSlice {
  byId: Class[];
  loading: boolean;
  error: string | null;
}

export interface SamplesSlice {
  byId: Sample[];
  loading: boolean;
  error: string | null;
}

export interface EvalSlice {
  byId: EvalSample[];
  loading: boolean;
  error: string | null;
}

export type TrainingState =
  | { status: 'idle' }
  | { status: 'loading-mobilenet' }
  | { status: 'extracting'; current: number; total: number }
  | {
      status: 'training';
      epoch: number;
      epochs: number;
      loss: number;
      accuracy: number;
      lossHistory: number[];
    }
  | { status: 'done'; trainedAt: number }
  | { status: 'error'; message: string };

export const IDLE_TRAINING: TrainingState = { status: 'idle' };

const EMPTY_CLASSES: ClassesSlice = { byId: [], loading: false, error: null };
const EMPTY_SAMPLES: SamplesSlice = { byId: [], loading: false, error: null };
const EMPTY_EVAL: EvalSlice = { byId: [], loading: false, error: null };

export interface ProjectsState {
  // projects
  projects: Project[];
  loading: boolean;
  error: string | null;
  pendingDelete: PendingDelete | null;
  load: () => Promise<void>;
  create: (input: NewProjectInput) => Promise<Project>;
  rename: (id: string, name: string) => Promise<void>;
  /** Persists the kid's block program for the Behavior tab. Pass `null`
   *  to clear it. The store's `projects` slice updates immediately. */
  setBlockProgram: (id: string, program: BlockProgram | null) => Promise<void>;
  /** Persists the kid's publish-composer config (template + theme +
   *  text + per-class flair). Pass `null` to clear. */
  setPublishConfig: (
    id: string,
    config: import('../publish/types').PublishConfig | null,
  ) => Promise<void>;
  /** Phase 4.5b — bind a local project to its server-side mirror so
   *  the SyncButton can avoid creating duplicates. Idempotent. */
  setServerId: (id: string, serverId: string) => Promise<void>;
  scheduleProjectDelete: (id: string, undoWindowMs?: number) => void;
  undoDelete: () => void;
  flushPendingDelete: () => Promise<void>;

  // classes — keyed by projectId
  classesByProject: Record<string, ClassesSlice>;
  loadClasses: (projectId: string) => Promise<void>;
  addClass: (projectId: string, name: string) => Promise<Class>;
  renameClass: (projectId: string, id: string, name: string) => Promise<void>;
  setClassCaptureSettings: (
    projectId: string,
    id: string,
    settings: ClassCaptureSettings,
  ) => Promise<void>;
  setClassDisabled: (projectId: string, id: string, disabled: boolean) => Promise<void>;
  scheduleClassDelete: (projectId: string, id: string, undoWindowMs?: number) => void;

  // samples — keyed by classId
  samplesByClass: Record<string, SamplesSlice>;
  loadSamples: (classId: string) => Promise<void>;
  /** Adds a sample. Pose projects pass `opts.poseKeypoints` so the
   *  thumbnail / modal overlay can render without re-running MoveNet. */
  addSample: (
    classId: string,
    blob: Blob,
    opts?: { poseKeypoints?: ReadonlyArray<import('../data/types').SamplePoseKeypoint> },
  ) => Promise<Sample>;
  /** Move a sample from `fromClassId` to `toClassId`. Updates both
   *  class slices in the store so the UI reflects the move
   *  immediately. */
  reassignSample: (fromClassId: string, sampleId: string, toClassId: string) => Promise<void>;
  deleteSample: (classId: string, sampleId: string) => Promise<void>;
  scheduleSamplesDelete: (classId: string, undoWindowMs?: number) => void;

  // trained models — keyed by projectId
  trainedModels: Record<string, TrainedModel>;
  loadTrainedModel: (projectId: string) => Promise<void>;
  /** Live training state, keyed by projectId. Falls back to `idle`. */
  trainingByProject: Record<string, TrainingState>;
  trainProject: (projectId: string, hyperparams?: TrainHyperparams) => Promise<void>;
  cancelTraining: (projectId: string) => void;
  /**
   * Triggers a browser download of the trained model for `projectId`.
   * Uses the project's current name to derive the filename and the trained
   * model's labels (frozen at training time) for the embedded metadata.
   */
  exportProject: (projectId: string) => Promise<void>;
  /**
   * Phase 2 PWA export — packages the trained classifier + the kid's
   * block program into a self-contained zip the kid can host on any
   * static-file service. Image projects only for now; throws
   * `unsupported-kind` for audio + pose. Triggers a browser download
   * directly.
   */
  exportPwa: (projectId: string) => Promise<void>;

  // eval samples — keyed by projectId
  evalByProject: Record<string, EvalSlice>;
  loadEvalSamples: (projectId: string) => Promise<void>;
  addEvalSample: (projectId: string, classId: string, blob: Blob) => Promise<EvalSample>;
  deleteEvalSample: (projectId: string, sampleId: string) => Promise<void>;

  // project file (.bryta) — packs project + classes + samples + eval into a
  // single zip. Trained model weights are intentionally NOT included; opening
  // a project always re-trains so the kid sees an honest, fresh model.
  exportProjectFile: (projectId: string) => Promise<Blob>;
  importProjectFile: (file: Blob | ArrayBuffer | Uint8Array) => Promise<Project>;

  /**
   * Fork a remote gallery submission into a fresh local project.
   * Carries over name + kind + class labels + block program. Sample
   * bytes are NOT copied — the remixer captures their own.
   */
  remixFromBundle: (bundle: {
    project_name: string;
    project_kind: 'image' | 'audio' | 'pose';
    block_program: BlockProgram | Record<string, unknown> | null;
    class_labels: string[];
  }) => Promise<Project>;
}

export type ProjectsStoreApi = StoreApi<ProjectsState>;

export interface MakeProjectsStoreOptions {
  storage?: ProjectStorage;
  trainer?: ImageTrainer;
  audioTrainer?: AudioTrainer;
  /** Pose classifier — same interface as the image trainer, different
   *  feature extractor (MoveNet keypoints instead of MobileNet
   *  embeddings). Optional so the store stays usable without it. */
  poseTrainer?: ImageTrainer;
}

const DEFAULT_UNDO_WINDOW_MS = 5_000;

const NOOP_TRAINER: ImageTrainer = {
  async train() {
    throw new Error('No trainer configured for this store.');
  },
  async removeModel() {
    /* no-op */
  },
  async loadModel() {
    return null;
  },
  async predict() {
    throw new Error('No trainer configured for this store.');
  },
  disposeModel() {
    /* no-op */
  },
  async exportModel() {
    throw new Error('No trainer configured for this store.');
  },
};

const NOOP_AUDIO_TRAINER: AudioTrainer = {
  async train() {
    throw new Error('No audio trainer configured for this store.');
  },
  async removeModel() {
    /* no-op */
  },
  async loadModel() {
    return null;
  },
  async predictAudio() {
    throw new Error('No audio trainer configured for this store.');
  },
  disposeModel() {
    /* no-op */
  },
  async exportModel() {
    throw new Error('No audio trainer configured for this store.');
  },
};

export function makeProjectsStore({
  storage = dexieStorage,
  trainer = NOOP_TRAINER,
  audioTrainer = NOOP_AUDIO_TRAINER,
  poseTrainer = NOOP_TRAINER,
}: MakeProjectsStoreOptions = {}): ProjectsStoreApi {
  const abortControllers = new Map<string, AbortController>();
  return create<ProjectsState>((set, get) => ({
    projects: [],
    loading: false,
    error: null,
    pendingDelete: null,
    classesByProject: {},
    samplesByClass: {},
    trainedModels: {},
    trainingByProject: {},
    evalByProject: {},

    async load() {
      set({ loading: true, error: null });
      try {
        const projects = await storage.list();
        set({ projects, loading: false });
      } catch (err) {
        set({ loading: false, error: errorMessage(err) });
      }
    },

    async create(input) {
      const project = await storage.create(input);
      set((state) => ({ projects: [project, ...state.projects] }));
      capture('project_created', {
        kind: project.kind,
        from_remix: false,
        title_length_bucket: bucketLength(input.name),
        project_local_id: shortHash(project.id),
      });
      return project;
    },

    async rename(id, name) {
      const renamed = await storage.rename(id, name);
      set((state) => ({
        projects: state.projects.map((p) => (p.id === id ? renamed : p)),
      }));
    },

    async setBlockProgram(id, program) {
      const updated = await storage.setBlockProgram(id, program);
      set((state) => ({
        projects: state.projects.map((p) => (p.id === id ? updated : p)),
      }));
    },

    async setPublishConfig(id, config) {
      const updated = await storage.setPublishConfig(id, config);
      set((state) => ({
        projects: state.projects.map((p) => (p.id === id ? updated : p)),
      }));
    },

    async setServerId(id, serverId) {
      const updated = await storage.setServerId(id, serverId);
      set((state) => ({
        projects: state.projects.map((p) => (p.id === id ? updated : p)),
      }));
    },

    scheduleProjectDelete(id, undoWindowMs = DEFAULT_UNDO_WINDOW_MS) {
      void replacePendingDelete(get, storage);
      const project = get().projects.find((p) => p.id === id);
      if (!project) return;

      const timeoutHandle = setTimeout(() => {
        void get().flushPendingDelete();
      }, undoWindowMs);

      set((state) => ({
        projects: state.projects.filter((p) => p.id !== id),
        pendingDelete: {
          kind: 'project',
          project,
          expiresAt: Date.now() + undoWindowMs,
          timeoutHandle,
        },
      }));
    },

    scheduleClassDelete(projectId, id, undoWindowMs = DEFAULT_UNDO_WINDOW_MS) {
      void replacePendingDelete(get, storage);
      const slice = currentClassSlice(get(), projectId);
      const classIndex = slice.byId.findIndex((c) => c.id === id);
      if (classIndex < 0) return;
      const cls = slice.byId[classIndex]!;

      const timeoutHandle = setTimeout(() => {
        void get().flushPendingDelete();
      }, undoWindowMs);

      set((state) => ({
        classesByProject: {
          ...state.classesByProject,
          [projectId]: {
            ...slice,
            byId: slice.byId.filter((c) => c.id !== id),
          },
        },
        pendingDelete: {
          kind: 'class',
          projectId,
          class: cls,
          classIndex,
          expiresAt: Date.now() + undoWindowMs,
          timeoutHandle,
        },
      }));
    },

    scheduleSamplesDelete(classId, undoWindowMs = DEFAULT_UNDO_WINDOW_MS) {
      void replacePendingDelete(get, storage);
      const slice = currentSampleSlice(get(), classId);
      if (slice.byId.length === 0) return;
      const samples = slice.byId;

      const timeoutHandle = setTimeout(() => {
        void get().flushPendingDelete();
      }, undoWindowMs);

      set((state) => ({
        samplesByClass: {
          ...state.samplesByClass,
          [classId]: { ...slice, byId: [] },
        },
        pendingDelete: {
          kind: 'samples-batch',
          classId,
          samples,
          expiresAt: Date.now() + undoWindowMs,
          timeoutHandle,
        },
      }));
    },

    undoDelete() {
      const { pendingDelete } = get();
      if (!pendingDelete) return;
      clearTimeout(pendingDelete.timeoutHandle);
      switch (pendingDelete.kind) {
        case 'project':
          set((state) => ({
            projects: insertSorted(state.projects, pendingDelete.project),
            pendingDelete: null,
          }));
          break;
        case 'class':
          set((state) => {
            const slice = currentClassSlice(state, pendingDelete.projectId);
            const next = [...slice.byId];
            next.splice(pendingDelete.classIndex, 0, pendingDelete.class);
            return {
              classesByProject: {
                ...state.classesByProject,
                [pendingDelete.projectId]: { ...slice, byId: next },
              },
              pendingDelete: null,
            };
          });
          break;
        case 'samples-batch':
          set((state) => {
            const slice = currentSampleSlice(state, pendingDelete.classId);
            return {
              samplesByClass: {
                ...state.samplesByClass,
                [pendingDelete.classId]: { ...slice, byId: pendingDelete.samples },
              },
              pendingDelete: null,
            };
          });
          break;
      }
    },

    async flushPendingDelete() {
      const { pendingDelete } = get();
      if (!pendingDelete) return;
      clearTimeout(pendingDelete.timeoutHandle);
      try {
        switch (pendingDelete.kind) {
          case 'project': {
            const hadTrainedModel = !!get().trainedModels[pendingDelete.project.id];
            await storage.delete(pendingDelete.project.id);
            capture('project_deleted', {
              kind: pendingDelete.project.kind,
              had_trained_model: hadTrainedModel,
              project_local_id: shortHash(pendingDelete.project.id),
            });
            // Both trainers' removeModel is idempotent and silent when the
            // model isn't there, so it's fine to ask both regardless of
            // the project's kind.
            await trainer.removeModel(pendingDelete.project.id);
            await audioTrainer.removeModel(pendingDelete.project.id);
            await poseTrainer.removeModel(pendingDelete.project.id);
            set((state) => {
              const nextClasses = { ...state.classesByProject };
              const droppedClasses = nextClasses[pendingDelete.project.id];
              delete nextClasses[pendingDelete.project.id];
              const nextSamples = pruneSamplesForClasses(
                state.samplesByClass,
                droppedClasses?.byId ?? [],
              );
              const nextTrained = { ...state.trainedModels };
              delete nextTrained[pendingDelete.project.id];
              const nextTraining = { ...state.trainingByProject };
              delete nextTraining[pendingDelete.project.id];
              const nextEval = { ...state.evalByProject };
              delete nextEval[pendingDelete.project.id];
              return {
                pendingDelete: null,
                classesByProject: nextClasses,
                samplesByClass: nextSamples,
                trainedModels: nextTrained,
                trainingByProject: nextTraining,
                evalByProject: nextEval,
              };
            });
            break;
          }
          case 'class': {
            const hadSamples = (get().samplesByClass[pendingDelete.class.id]?.byId.length ?? 0) > 0;
            await storage.deleteClass(pendingDelete.class.id);
            capture('class_deleted', {
              class_count_after: get().classesByProject[pendingDelete.projectId]?.byId.length ?? 0,
              had_samples: hadSamples,
              project_local_id: shortHash(pendingDelete.projectId),
            });
            set((state) => {
              const nextSamples = { ...state.samplesByClass };
              delete nextSamples[pendingDelete.class.id];
              return { pendingDelete: null, samplesByClass: nextSamples };
            });
            break;
          }
          case 'samples-batch': {
            for (const sample of pendingDelete.samples) {
              await storage.deleteSample(sample.id);
            }
            capture('samples_deleted', { count: pendingDelete.samples.length });
            set({ pendingDelete: null });
            break;
          }
        }
      } catch (err) {
        set({ error: errorMessage(err), pendingDelete: null });
      }
    },

    async loadClasses(projectId) {
      set((state) => ({
        classesByProject: {
          ...state.classesByProject,
          [projectId]: { ...currentClassSlice(state, projectId), loading: true, error: null },
        },
      }));
      try {
        const list = await storage.listClasses(projectId);
        set((state) => ({
          classesByProject: {
            ...state.classesByProject,
            [projectId]: { byId: list, loading: false, error: null },
          },
        }));
      } catch (err) {
        set((state) => ({
          classesByProject: {
            ...state.classesByProject,
            [projectId]: {
              ...currentClassSlice(state, projectId),
              loading: false,
              error: errorMessage(err),
            },
          },
        }));
      }
    },

    async addClass(projectId, name) {
      const cls = await storage.createClass(projectId, name);
      set((state) => {
        const slice = currentClassSlice(state, projectId);
        return {
          classesByProject: {
            ...state.classesByProject,
            [projectId]: { ...slice, byId: [...slice.byId, cls] },
          },
        };
      });
      capture('class_added', {
        class_count_after: get().classesByProject[projectId]?.byId.length ?? 0,
        project_local_id: shortHash(projectId),
      });
      return cls;
    },

    async renameClass(projectId, id, name) {
      const renamed = await storage.renameClass(id, name);
      capture('class_renamed', {
        class_count: get().classesByProject[projectId]?.byId.length ?? 0,
        project_local_id: shortHash(projectId),
      });
      set((state) => {
        const slice = currentClassSlice(state, projectId);
        return {
          classesByProject: {
            ...state.classesByProject,
            [projectId]: {
              ...slice,
              byId: slice.byId.map((cls) => (cls.id === id ? renamed : cls)),
            },
          },
        };
      });
    },

    async setClassCaptureSettings(projectId, id, settings) {
      const updated = await storage.setClassCaptureSettings(id, settings);
      set((state) => {
        const slice = currentClassSlice(state, projectId);
        return {
          classesByProject: {
            ...state.classesByProject,
            [projectId]: {
              ...slice,
              byId: slice.byId.map((cls) => (cls.id === id ? updated : cls)),
            },
          },
        };
      });
    },

    async setClassDisabled(projectId, id, disabled) {
      const updated = await storage.setClassDisabled(id, disabled);
      set((state) => {
        const slice = currentClassSlice(state, projectId);
        return {
          classesByProject: {
            ...state.classesByProject,
            [projectId]: {
              ...slice,
              byId: slice.byId.map((cls) => (cls.id === id ? updated : cls)),
            },
          },
        };
      });
    },

    async loadSamples(classId) {
      set((state) => ({
        samplesByClass: {
          ...state.samplesByClass,
          [classId]: { ...currentSampleSlice(state, classId), loading: true, error: null },
        },
      }));
      try {
        const list = await storage.listSamples(classId);
        set((state) => ({
          samplesByClass: {
            ...state.samplesByClass,
            [classId]: { byId: list, loading: false, error: null },
          },
        }));
      } catch (err) {
        set((state) => ({
          samplesByClass: {
            ...state.samplesByClass,
            [classId]: {
              ...currentSampleSlice(state, classId),
              loading: false,
              error: errorMessage(err),
            },
          },
        }));
      }
    },

    async addSample(classId, blob, opts) {
      const sample = await storage.addSample(classId, blob, opts);
      set((state) => {
        const slice = currentSampleSlice(state, classId);
        return {
          samplesByClass: {
            ...state.samplesByClass,
            [classId]: { ...slice, byId: [...slice.byId, sample] },
          },
        };
      });
      return sample;
    },

    async reassignSample(fromClassId, sampleId, toClassId) {
      const updated = await storage.reassignSample(sampleId, toClassId);
      set((state) => {
        const fromSlice = currentSampleSlice(state, fromClassId);
        const toSlice = currentSampleSlice(state, toClassId);
        return {
          samplesByClass: {
            ...state.samplesByClass,
            [fromClassId]: {
              ...fromSlice,
              byId: fromSlice.byId.filter((s) => s.id !== sampleId),
            },
            [toClassId]: {
              ...toSlice,
              byId: [...toSlice.byId, updated],
            },
          },
        };
      });
    },

    async deleteSample(classId, sampleId) {
      await storage.deleteSample(sampleId);
      set((state) => {
        const slice = currentSampleSlice(state, classId);
        return {
          samplesByClass: {
            ...state.samplesByClass,
            [classId]: {
              ...slice,
              byId: slice.byId.filter((s) => s.id !== sampleId),
            },
          },
        };
      });
    },

    async loadTrainedModel(projectId) {
      const model = await storage.getTrainedModel(projectId);
      if (!model) return;
      set((state) => ({
        trainedModels: { ...state.trainedModels, [projectId]: model },
        trainingByProject: {
          ...state.trainingByProject,
          [projectId]: { status: 'done', trainedAt: model.trainedAt },
        },
      }));
    },

    async trainProject(projectId, hyperparams) {
      const state = get();
      // Disabled classes stay in storage but do NOT participate in
      // training — TM-style A/B switch for whether a class is helping
      // the model.
      const classes = (state.classesByProject[projectId]?.byId ?? []).filter(
        (cls) => !cls.disabled,
      );
      const project = state.projects.find((p) => p.id === projectId);
      const isAudio = project?.kind === 'audio';
      const isPose = project?.kind === 'pose';
      // Make sure we have the latest samples for every class.
      for (const cls of classes) {
        if (state.samplesByClass[cls.id] === undefined) {
          await get().loadSamples(cls.id);
        }
      }
      const refreshed = get();
      const allSamples = classes.flatMap((cls) => refreshed.samplesByClass[cls.id]?.byId ?? []);

      // Cancel any in-flight training for the same project.
      abortControllers.get(projectId)?.abort();
      const controller = new AbortController();
      abortControllers.set(projectId, controller);

      const setTrainingState = (next: TrainingState): void => {
        set((current) => ({
          trainingByProject: { ...current.trainingByProject, [projectId]: next },
        }));
      };

      setTrainingState({ status: 'loading-mobilenet' });

      const trainStartedAt = Date.now();
      const samplesPerClass = classes.map(
        (cls) => refreshed.samplesByClass[cls.id]?.byId.length ?? 0,
      );
      const median = (arr: number[]): number => {
        if (arr.length === 0) return 0;
        const sorted = [...arr].sort((a, b) => a - b);
        const mid = Math.floor(sorted.length / 2);
        return sorted.length % 2 === 0
          ? Math.round((sorted[mid - 1]! + sorted[mid]!) / 2)
          : sorted[mid]!;
      };
      capture('training_started', {
        kind: project?.kind ?? 'image',
        class_count: classes.length,
        total_samples: allSamples.length,
        samples_per_class_median: median(samplesPerClass),
        project_local_id: shortHash(projectId),
      });

      const onProgress = (
        event: TrainProgress | import('../ml/audioTrainer').AudioTrainProgress,
      ): void => {
        if (controller.signal.aborted) return;
        switch (event.phase) {
          case 'loading-mobilenet':
          case 'preparing':
            setTrainingState({ status: 'loading-mobilenet' });
            break;
          case 'extracting':
            setTrainingState({
              status: 'extracting',
              current: event.current,
              total: event.total,
            });
            break;
          case 'training': {
            const prev = get().trainingByProject[projectId];
            const history =
              prev && prev.status === 'training' ? [...prev.lossHistory, event.loss] : [event.loss];
            setTrainingState({
              status: 'training',
              epoch: event.epoch,
              epochs: event.epochs,
              loss: event.loss,
              accuracy: event.accuracy,
              lossHistory: history,
            });
            break;
          }
        }
      };

      try {
        // Conditionally spread hyperparams so we don't pass `undefined`
        // under `exactOptionalPropertyTypes`.
        const baseInput = {
          projectId,
          classes,
          samples: allSamples,
          ...(hyperparams ? { hyperparams } : {}),
          onProgress,
          signal: controller.signal,
        };
        const result = isAudio
          ? await audioTrainer.train(baseInput)
          : isPose
            ? await poseTrainer.train(baseInput)
            : await trainer.train(baseInput);
        const trainedModel: TrainedModel = {
          projectId,
          labels: result.labels,
          sampleCounts: result.sampleCounts,
          trainedAt: Date.now(),
          converged: result.converged,
          finalLoss: result.finalLoss,
          finalAccuracy: result.finalAccuracy,
          lossHistory: result.lossHistory,
        };
        await storage.saveTrainedModel(trainedModel);
        set((current) => ({
          trainedModels: { ...current.trainedModels, [projectId]: trainedModel },
          trainingByProject: {
            ...current.trainingByProject,
            [projectId]: { status: 'done', trainedAt: trainedModel.trainedAt },
          },
        }));
        const durationMs = Date.now() - trainStartedAt;
        capture('training_completed', {
          kind: project?.kind ?? 'image',
          class_count: classes.length,
          duration_ms: durationMs,
          duration_bucket: bucketDuration(durationMs),
          final_accuracy_bucket: bucketAccuracy(trainedModel.finalAccuracy ?? 0),
          epoch_count: trainedModel.lossHistory?.length ?? 0,
          converged: trainedModel.converged,
          project_local_id: shortHash(projectId),
        });
      } catch (err) {
        const durationMs = Date.now() - trainStartedAt;
        if (err instanceof DOMException && err.name === 'AbortError') {
          // Cancellation isn't an error to surface; reset to idle.
          setTrainingState({ status: 'idle' });
          capture('training_canceled', {
            kind: project?.kind ?? 'image',
            duration_ms: durationMs,
            project_local_id: shortHash(projectId),
          });
          return;
        }
        setTrainingState({ status: 'error', message: errorMessage(err) });
        capture('training_failed', {
          kind: project?.kind ?? 'image',
          duration_ms: durationMs,
          error_kind: classifyTrainingError(err),
          project_local_id: shortHash(projectId),
        });
      } finally {
        if (abortControllers.get(projectId) === controller) {
          abortControllers.delete(projectId);
        }
      }
    },

    cancelTraining(projectId) {
      abortControllers.get(projectId)?.abort();
    },

    async exportProject(projectId) {
      const state = get();
      const project = state.projects.find((p) => p.id === projectId);
      const trainedModel = state.trainedModels[projectId];
      if (!project) {
        throw new Error('Project not found.');
      }
      if (!trainedModel) {
        throw new Error('No trained model to export. Train it first.');
      }
      const exporter =
        project.kind === 'audio' ? audioTrainer : project.kind === 'pose' ? poseTrainer : trainer;
      await exporter.exportModel({
        projectId,
        projectName: project.name,
        labels: trainedModel.labels,
      });
      capture('model_downloaded', {
        kind: project.kind,
        format: 'zip',
        project_local_id: shortHash(projectId),
      });
    },

    async exportPwa(projectId) {
      const state = get();
      const project = state.projects.find((p) => p.id === projectId);
      const trainedModel = state.trainedModels[projectId];
      if (!project) throw new Error('Project not found.');
      if (!trainedModel) throw new Error('No trained model to ship. Train it first.');
      if (project.kind !== 'image') {
        throw new Error(
          `PWA export for ${project.kind} projects is not ready yet — try with a Camera project.`,
        );
      }
      const program = project.blockProgram ?? { schemaVersion: 1, rules: [] };
      const { downloadPwaZip } = await import('../behavior/pwa/buildPwaZip');
      await downloadPwaZip({
        projectId,
        projectName: project.name,
        labels: trainedModel.labels,
        blockProgram: program,
        trainer,
      });
      capture('website_made', {
        kind: project.kind,
        rule_count: program.rules.length,
        project_local_id: shortHash(projectId),
      });
    },

    async loadEvalSamples(projectId) {
      set((state) => ({
        evalByProject: {
          ...state.evalByProject,
          [projectId]: { ...currentEvalSlice(state, projectId), loading: true, error: null },
        },
      }));
      try {
        const list = await storage.listEvalSamples(projectId);
        set((state) => ({
          evalByProject: {
            ...state.evalByProject,
            [projectId]: { byId: list, loading: false, error: null },
          },
        }));
      } catch (err) {
        set((state) => ({
          evalByProject: {
            ...state.evalByProject,
            [projectId]: {
              ...currentEvalSlice(state, projectId),
              loading: false,
              error: errorMessage(err),
            },
          },
        }));
      }
    },

    async addEvalSample(projectId, classId, blob) {
      const sample = await storage.addEvalSample(projectId, classId, blob);
      set((state) => {
        const slice = currentEvalSlice(state, projectId);
        return {
          evalByProject: {
            ...state.evalByProject,
            [projectId]: { ...slice, byId: [...slice.byId, sample] },
          },
        };
      });
      capture('quiz_sample_added', {
        total_held_out_samples_after: get().evalByProject[projectId]?.byId.length ?? 0,
        project_local_id: shortHash(projectId),
      });
      return sample;
    },

    async deleteEvalSample(projectId, sampleId) {
      await storage.deleteEvalSample(sampleId);
      set((state) => {
        const slice = currentEvalSlice(state, projectId);
        return {
          evalByProject: {
            ...state.evalByProject,
            [projectId]: {
              ...slice,
              byId: slice.byId.filter((s) => s.id !== sampleId),
            },
          },
        };
      });
    },

    async exportProjectFile(projectId) {
      const state = get();
      const project = state.projects.find((p) => p.id === projectId);
      if (!project) throw new Error('Project not found.');
      // Make sure classes + per-class samples + eval are loaded.
      if (state.classesByProject[projectId] === undefined) {
        await get().loadClasses(projectId);
      }
      const classes = get().classesByProject[projectId]?.byId ?? [];
      for (const cls of classes) {
        if (get().samplesByClass[cls.id] === undefined) {
          await get().loadSamples(cls.id);
        }
      }
      if (state.evalByProject[projectId] === undefined) {
        await get().loadEvalSamples(projectId);
      }
      const refreshed = get();
      const samples = classes.flatMap((c) => refreshed.samplesByClass[c.id]?.byId ?? []);
      const evalSamples = refreshed.evalByProject[projectId]?.byId ?? [];
      return packProjectFile({ project, classes, samples, evalSamples });
    },

    async importProjectFile(file) {
      const decoded = await unpackProjectFile(file);
      // 1. Create the project. Storage assigns a fresh ID.
      const project = await get().create(decoded.projectInput);
      // 2. Recreate classes in their original order. Audio projects ship
      //    with an auto-created "Background noise" class via storage — read
      //    the live list directly from storage (the in-memory class slice
      //    is still empty at this point) and drop them so the imported
      //    classes land verbatim and we don't double up.
      const auto = await storage.listClasses(project.id);
      for (const cls of auto) {
        await storage.deleteClass(cls.id);
      }
      const idMap = new Map<string, string>(); // originalId → newId
      for (const cls of decoded.classes) {
        const created = await get().addClass(project.id, cls.name);
        idMap.set(cls.originalId, created.id);
      }
      // 3. Restore training samples under their new class IDs.
      for (const [origId, blobs] of Object.entries(decoded.samplesByOriginalClassId)) {
        const newId = idMap.get(origId);
        if (!newId) continue;
        for (const { blob } of blobs) {
          await get().addSample(newId, blob);
        }
      }
      // 4. Restore eval samples (truth = original class id).
      for (const [origId, blobs] of Object.entries(decoded.evalSamplesByOriginalClassId)) {
        const newId = idMap.get(origId);
        if (!newId) continue;
        for (const { blob } of blobs) {
          await get().addEvalSample(project.id, newId, blob);
        }
      }
      return project;
    },

    async remixFromBundle(bundle) {
      // The original project name is preserved verbatim — the kid
      // sees a "Remixed" badge on the card / title rather than a
      // "(remix)" suffix on the name. Cap to the same 120-char
      // limit the kid's own create flow uses so a long name doesn't
      // trip storage.
      const name = bundle.project_name.slice(0, 120);
      const project = await get().create({
        name,
        kind: bundle.project_kind,
      });
      // Stamp the new project as a remix so the UI can render the
      // badge. We do this *after* create so the suffix-free flow
      // still shows up as a remix even if the kid never opens the
      // project view.
      const stamped = await storage.markRemixed(project.id);
      set((state) => ({
        projects: state.projects.map((p) => (p.id === stamped.id ? stamped : p)),
      }));
      // Drop any auto-created classes (audio adds "Background
      // noise") so the remix's class list lines up with the
      // original project's labels exactly.
      const auto = await storage.listClasses(project.id);
      for (const cls of auto) {
        await storage.deleteClass(cls.id);
      }
      for (const label of bundle.class_labels) {
        await get().addClass(project.id, label);
      }
      // Refresh the local class slice so the next render sees the
      // freshly-recreated rows instead of the dropped auto class.
      await get().loadClasses(project.id);
      if (bundle.block_program) {
        await get().setBlockProgram(project.id, bundle.block_program as BlockProgram);
      }
      return stamped;
    },
  }));
}

/**
 * If a pending delete is in-flight, commit it now. Used when a new schedule
 * is starting — only one pending delete can exist at a time.
 */
async function replacePendingDelete(
  get: () => ProjectsState,
  storage: ProjectStorage,
): Promise<void> {
  const existing = get().pendingDelete;
  if (!existing) return;
  clearTimeout(existing.timeoutHandle);
  try {
    switch (existing.kind) {
      case 'project':
        await storage.delete(existing.project.id);
        break;
      case 'class':
        await storage.deleteClass(existing.class.id);
        break;
      case 'samples-batch':
        for (const sample of existing.samples) {
          await storage.deleteSample(sample.id);
        }
        break;
    }
  } catch {
    // Best-effort: a failure to commit the displaced delete is logged via
    // the next flush path, not surfaced as an error message here, since the
    // user has already moved on to a new action.
  }
}

function currentClassSlice(state: ProjectsState, projectId: string): ClassesSlice {
  return state.classesByProject[projectId] ?? EMPTY_CLASSES;
}

function currentSampleSlice(state: ProjectsState, classId: string): SamplesSlice {
  return state.samplesByClass[classId] ?? EMPTY_SAMPLES;
}

function currentEvalSlice(state: ProjectsState, projectId: string): EvalSlice {
  return state.evalByProject[projectId] ?? EMPTY_EVAL;
}

function pruneSamplesForClasses(
  samples: Record<string, SamplesSlice>,
  classes: Class[],
): Record<string, SamplesSlice> {
  if (classes.length === 0) return samples;
  const next = { ...samples };
  for (const cls of classes) {
    delete next[cls.id];
  }
  return next;
}

function insertSorted(projects: Project[], project: Project): Project[] {
  const next = [...projects, project];
  next.sort((a, b) => b.updatedAt - a.updatedAt);
  return next;
}

function errorMessage(err: unknown): string {
  if (err instanceof Error) return err.message;
  return 'Something went wrong.';
}

/**
 * Bucket the few training failure shapes we can name into stable
 * labels for analytics. The actual message string is not sent —
 * it can carry filenames or shape data we'd rather keep private.
 */
function classifyTrainingError(err: unknown): 'oom' | 'no_samples' | 'unknown' {
  if (!(err instanceof Error)) return 'unknown';
  const msg = err.message.toLowerCase();
  if (msg.includes('out of memory') || msg.includes('oom')) return 'oom';
  if (msg.includes('no samples') || msg.includes('not enough')) return 'no_samples';
  return 'unknown';
}
